import React, { useState, useEffect } from 'react';
import { format, subDays, isValid } from 'date-fns';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface Punch {
  id: string;
  punch_time: string;
  punch_type: 'in' | 'out';
  source: string;
}

interface ShiftRecord {
  id: string;
  employee_id: string;
  employee_name: string;
  date: string;
  start_time: string;
  end_time: string;
  clock_in_time: string | null;
  clock_out_time: string | null;
  hours_worked: number | null;
  status: string;
  workstation_name: string | null;
  punches?: Punch[];
}

interface Employee {
  id: string;
  name: string;
}

export function TimeManagementPage() {
  const [shifts, setShifts] = useState<ShiftRecord[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [editingShift, setEditingShift] = useState<ShiftRecord | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [corrections, setCorrections] = useState<any[]>([]);
  // Company holidays for the surrounding year, resolved to real dates by the
  // API. Same source the payroll holiday credit reads, so the two cannot
  // disagree about which days the plant is closed.
  const [holidays, setHolidays] = useState<Array<{ name: string; date: string; halfDay: boolean; hours: number }>>([]);
  const [corrEdit, setCorrEdit] = useState<Record<string, string>>({});

  const toggleExpand = (employeeId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  // Pair a day's punches into work sessions. Gaps = breaks. Running session if still clocked in.
  const computeSessions = (punches: Punch[]) => {
    const sorted = [...punches].sort((a, b) => new Date(a.punch_time).getTime() - new Date(b.punch_time).getTime());
    const sessions: { inTime: string; outTime: string | null; minutes: number }[] = [];
    const breaks: { start: string; end: string; minutes: number }[] = [];
    let totalWorkMin = 0;
    let totalBreakMin = 0;

    for (let i = 0; i < sorted.length; i++) {
      const p = sorted[i];
      if (p.punch_type === 'in') {
        const nextOut = sorted[i + 1]?.punch_type === 'out' ? sorted[i + 1] : null;
        const inT = new Date(p.punch_time);
        const outT = nextOut ? new Date(nextOut.punch_time) : null;
        const mins = outT ? (outT.getTime() - inT.getTime()) / 60000 : (Date.now() - inT.getTime()) / 60000;
        sessions.push({ inTime: p.punch_time, outTime: nextOut?.punch_time || null, minutes: mins });
        totalWorkMin += mins;
        if (nextOut) i++;
      }
    }
    for (let i = 1; i < sessions.length; i++) {
      const prevOut = sessions[i - 1].outTime;
      if (prevOut) {
        const bmins = (new Date(sessions[i].inTime).getTime() - new Date(prevOut).getTime()) / 60000;
        breaks.push({ start: prevOut, end: sessions[i].inTime, minutes: bmins });
        totalBreakMin += bmins;
      }
    }
    return { sessions, breaks, totalWorkMin, totalBreakMin };
  };

  const formatMin = (m: number) => {
    const totalMin = Math.round(m);
    const h = Math.floor(totalMin / 60);
    const min = totalMin % 60;
    return h > 0 ? `${h}h ${min}m` : `${min}m`;
  };
  const formatPunchTime = (s: string) => {
    const d = new Date(s);
    return isValid(d) ? format(d, 'h:mm a') : '--';
  };
  const [newShift, setNewShift] = useState({
    employee_id: '',
    shift_date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '07:00',
    end_time: '15:30',
  });
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [breakForm, setBreakForm] = useState({
    employee_id: '',
    break_start: '12:00',
    break_end: '12:30',
  });

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  useEffect(() => { loadCorrections(); }, []);

  // Reload when the selected date moves into another year.
  useEffect(() => { loadHolidays(selectedDate.slice(0, 4)); }, [selectedDate.slice(0, 4)]);

  const loadHolidays = async (year: string) => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(
        `${API_URL}/payroll/holidays?start=${year}-01-01&end=${year}-12-31`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) setHolidays(await res.json());
    } catch {
      // A missing holiday list must not break the timesheet — markers just
      // don't render.
    }
  };

  const loadCorrections = async () => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/employee-shifts/admin/punch-corrections?status=pending`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setCorrections(await res.json());
    } catch { /* ignore */ }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Load shifts for selected date
      const shiftsRes = await fetch(`${API_URL}/employee-shifts/date/${selectedDate}`, { headers });
      if (shiftsRes.ok) {
        const data = await shiftsRes.json();
        setShifts(data || []);
      } else {
        // Mock data for demo
        setShifts([
          {
            id: '1',
            employee_id: 'emp-1',
            employee_name: 'John Smith',
            date: selectedDate,
            start_time: '07:00',
            end_time: '15:30',
            clock_in_time: '2026-01-19T07:02:00Z',
            clock_out_time: '2026-01-19T15:28:00Z',
            hours_worked: 8.43,
            status: 'completed',
            workstation_name: 'Tube Cut',
          },
          {
            id: '2',
            employee_id: 'emp-2',
            employee_name: 'Maria Garcia',
            date: selectedDate,
            start_time: '07:00',
            end_time: '15:30',
            clock_in_time: '2026-01-19T07:05:00Z',
            clock_out_time: null,
            hours_worked: null,
            status: 'in_progress',
            workstation_name: 'Assembly',
          },
          {
            id: '3',
            employee_id: 'emp-3',
            employee_name: 'Mike Johnson',
            date: selectedDate,
            start_time: '07:00',
            end_time: '15:30',
            clock_in_time: null,
            clock_out_time: null,
            hours_worked: null,
            status: 'absent',
            workstation_name: 'Material Cut',
          },
        ]);
      }

      // Load employees list
      try {
        const empRes = await fetch(`${API_URL}/employees`, { headers });
        if (empRes.ok) {
          const data = await empRes.json();
          setEmployees(data || []);
        }
      } catch {
        setEmployees([
          { id: 'emp-1', name: 'John Smith' },
          { id: 'emp-2', name: 'Maria Garcia' },
          { id: 'emp-3', name: 'Mike Johnson' },
        ]);
      }
    } catch (error) {
      console.error('Failed to load time data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '--';
    try {
      const date = new Date(dateStr);
      if (!isValid(date)) return dateStr;
      return format(date, 'h:mm a');
    } catch {
      return dateStr;
    }
  };

  const refreshEditModal = async (employeeId: string) => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/employee-shifts/date/${selectedDate}?employee_id=${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const match = data?.find((s: any) => s.employee_id === employeeId);
        if (match) setEditingShift(match);
      }
    } catch { /* ignore */ }
  };

  const handleSavePunchTime = async (punchId: string, newTime: string) => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const d = editingShift?.date || (editingShift as any)?.shift_date || selectedDate;
      const punchTime = new Date(`${d}T${newTime}:00`);
      const res = await fetch(`${API_URL}/employee-shifts/admin/punch/${punchId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ punch_time: punchTime.toISOString() }),
      });
      if (res.ok) { await loadData(); if (editingShift) await refreshEditModal(editingShift.employee_id); }
      else alert('Failed to update punch time');
    } catch { alert('Failed to update punch time'); }
  };

  const handleDeletePunch = async (punchId: string) => {
    if (!confirm('Delete this punch? The day will be re-sequenced.')) return;
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/employee-shifts/admin/punch/${punchId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { await loadData(); if (editingShift) await refreshEditModal(editingShift.employee_id); }
      else alert('Failed to delete punch');
    } catch { alert('Failed to delete punch'); }
  };

  const handleAddPunch = async (employeeId: string, punchType: 'in' | 'out', time: string) => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const punchTime = new Date(`${selectedDate}T${time}:00`);
      const res = await fetch(`${API_URL}/employee-shifts/admin/add-punch`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: employeeId, punch_time: punchTime.toISOString(), punch_type: punchType }),
      });
      if (res.ok) { await loadData(); await refreshEditModal(employeeId); }
      else alert('Failed to add punch');
    } catch { alert('Failed to add punch'); }
  };

  const handleAddBreak = async () => {
    if (!breakForm.employee_id || !breakForm.break_start || !breakForm.break_end) {
      alert('Employee, break start and break end are required');
      return;
    }
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/employee-shifts/admin/add-break`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: breakForm.employee_id,
          date: selectedDate,
          break_start: breakForm.break_start,
          break_end: breakForm.break_end,
        }),
      });
      if (res.ok) {
        setShowBreakModal(false);
        setBreakForm({ employee_id: '', break_start: '12:00', break_end: '12:30' });
        loadData();
      } else {
        const err = await res.json().catch(() => ({ message: 'Failed' }));
        alert(err.message || 'Failed to add break');
      }
    } catch (error) {
      console.error('Failed to add break:', error);
      alert('Failed to add break');
    }
  };

  const handleAddShift = async () => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/employee-shifts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newShift),
      });
      if (res.ok) {
        setShowAddModal(false);
        setNewShift({
          employee_id: '',
          shift_date: format(new Date(), 'yyyy-MM-dd'),
          start_time: '07:00',
          end_time: '15:30',
        });
        loadData();
      } else {
        alert('Failed to add time entry');
      }
    } catch (error) {
      console.error('Failed to add shift:', error);
      alert('Failed to add time entry');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      completed: 'bg-green-100 text-green-700',
      in_progress: 'bg-blue-100 text-blue-700',
      scheduled: 'bg-gray-100 text-gray-700',
      absent: 'bg-red-100 text-red-700',
      late: 'bg-orange-100 text-orange-700',
    };
    return styles[status] || styles.scheduled;
  };

  const localForInput = (iso: string) => {
    const d = new Date(iso); d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };
  const approveCorrection = async (c: any) => {
    const token = localStorage.getItem('admin_auth_token');
    const body: any = {};
    const edited = corrEdit[c.id];
    if (edited) body.punch_time = new Date(edited).toISOString();
    const res = await fetch(`${API_URL}/employee-shifts/admin/punch-corrections/${c.id}/approve`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body),
    });
    if (res.ok) { await loadCorrections(); await loadData(); } else { alert('Failed to apply correction'); }
  };
  const rejectCorrection = async (id: string) => {
    const note = window.prompt('Reason for rejecting (optional):') ?? '';
    const token = localStorage.getItem('admin_auth_token');
    const res = await fetch(`${API_URL}/employee-shifts/admin/punch-corrections/${id}/reject`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ review_note: note }),
    });
    if (res.ok) loadCorrections(); else alert('Failed to reject');
  };

  const selectedHoliday = holidays.find(h => h.date === selectedDate) || null;
  const upcomingHoliday = holidays
    .filter(h => h.date > selectedDate)
    .sort((a, b) => a.date.localeCompare(b.date))[0] || null;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Time Management</h1>
          <p className="text-gray-500">Edit employee clock in/out times</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowBreakModal(true)}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
          >
            + Add Break
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + Schedule Shift
          </button>
        </div>
      </div>

      {/* Employee punch-correction requests (forgot/missed punch) — review & apply */}
      {corrections.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-300 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-amber-900 mb-3">
            Punch Correction Requests <span className="ml-1 text-xs font-normal bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">{corrections.length} pending</span>
          </h2>
          <div className="space-y-3">
            {corrections.map((c) => (
              <div key={c.id} className="bg-white border border-amber-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">{c.employee_name}</div>
                  <div className="text-sm text-gray-600">
                    Requested <strong>{String(c.punch_type).toUpperCase()}</strong> at {new Date(c.requested_punch_time).toLocaleString()}
                  </div>
                  {c.reason && <div className="text-sm text-gray-500 italic mt-0.5">"{c.reason}"</div>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="text-xs text-gray-500">Adjust time
                    <input
                      type="datetime-local"
                      value={corrEdit[c.id] ?? localForInput(c.requested_punch_time)}
                      onChange={(e) => setCorrEdit({ ...corrEdit, [c.id]: e.target.value })}
                      className="block mt-0.5 border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </label>
                  <button onClick={() => approveCorrection(c)} className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700">Apply</button>
                  <button onClick={() => rejectCorrection(c.id)} className="px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded hover:bg-red-200">Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company holiday banner for the selected day */}
      {selectedHoliday && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <span className="text-xl leading-none">&#127881;</span>
            <div>
              <p className="text-sm font-semibold text-red-800">
                Company Holiday &mdash; {selectedHoliday.name}
                {selectedHoliday.halfDay && ' (half day)'}
              </p>
              <p className="text-xs text-red-700 mt-0.5">
                Plant closed. Employees marked <strong>Paid company holidays</strong> in Payroll are
                credited {selectedHoliday.hours} hours for this day. Anyone who punched in is treated
                as working a normal day and receives no holiday credit.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Date Selector */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Select Date:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedDate(format(subDays(new Date(`${selectedDate}T12:00:00`), 1), 'yyyy-MM-dd'))}
              className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded"
            >
              &larr; Previous
            </button>
            <button
              onClick={() => setSelectedDate(format(new Date(), 'yyyy-MM-dd'))}
              className="px-3 py-2 text-blue-600 hover:bg-blue-50 rounded"
            >
              Today
            </button>
            <button
              onClick={() => setSelectedDate(format(new Date(new Date(`${selectedDate}T12:00:00`).getTime() + 86400000), 'yyyy-MM-dd'))}
              className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded"
            >
              Next &rarr;
            </button>
          </div>
          {upcomingHoliday && !selectedHoliday && (
            <button
              onClick={() => setSelectedDate(upcomingHoliday.date)}
              className="ml-auto text-xs text-gray-500 hover:text-red-700 hover:underline"
              title="Jump to this holiday"
            >
              Next company holiday: <strong>{upcomingHoliday.name}</strong> on {upcomingHoliday.date}
              {upcomingHoliday.halfDay && ' (half day)'}
            </button>
          )}
        </div>
      </div>

      {/* Shifts Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : shifts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No time entries for this date</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Employee</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Scheduled</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Clock In</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Clock Out</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Hours</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Workstation</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {shifts.map((shift) => {
                const punches = shift.punches || [];
                const { sessions, breaks, totalWorkMin, totalBreakMin } = computeSessions(punches);
                const isExpanded = expandedRows.has(shift.employee_id);
                const canExpand = punches.length > 0;

                return (
                  <React.Fragment key={shift.employee_id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {canExpand && (
                          <button
                            onClick={() => toggleExpand(shift.employee_id)}
                            className="mr-2 text-gray-400 hover:text-gray-600"
                            title={isExpanded ? 'Collapse' : 'Expand sessions'}
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                        )}
                        {shift.employee_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {shift.start_time ? `${shift.start_time} - ${shift.end_time}` : '--'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {shift.clock_in_time ? (
                          <span className="text-green-600">{formatDateTime(shift.clock_in_time)}</span>
                        ) : (
                          <span className="text-red-500">Not clocked in</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {shift.clock_out_time ? (
                          <span className="text-green-600">{formatDateTime(shift.clock_out_time)}</span>
                        ) : shift.clock_in_time ? (
                          <span className="text-blue-500">Still working</span>
                        ) : (
                          <span className="text-gray-400">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {totalWorkMin > 0 ? (
                          <div>
                            <div className="font-medium">{formatMin(totalWorkMin)}</div>
                            {totalBreakMin > 0 && (
                              <div className="text-xs text-orange-600">+{formatMin(totalBreakMin)} break</div>
                            )}
                          </div>
                        ) : '--'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(shift.status)}`}>
                          {shift.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{shift.workstation_name || '--'}</td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => setEditingShift(shift)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                    {isExpanded && canExpand && (
                      <tr className="bg-gray-50">
                        <td colSpan={8} className="px-4 py-3">
                          <div className="ml-6 space-y-1">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                              Sessions ({sessions.length})
                            </div>
                            {sessions.map((s, idx) => (
                              <div key={`s-${idx}`} className="flex items-center gap-4 text-sm">
                                <span className={`w-2 h-2 rounded-full ${s.outTime ? 'bg-gray-400' : 'bg-green-500'}`} />
                                <span className="text-gray-900 font-medium min-w-[90px]">{formatPunchTime(s.inTime)}</span>
                                <span className="text-gray-400">→</span>
                                <span className={`min-w-[90px] ${s.outTime ? 'text-gray-900 font-medium' : 'text-green-600'}`}>
                                  {s.outTime ? formatPunchTime(s.outTime) : 'In progress'}
                                </span>
                                <span className="text-gray-600">{formatMin(s.minutes)}</span>
                              </div>
                            ))}
                            {breaks.length > 0 && (
                              <>
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mt-3 mb-2">
                                  Breaks ({breaks.length})
                                </div>
                                {breaks.map((b, idx) => (
                                  <div key={`b-${idx}`} className="flex items-center gap-4 text-sm">
                                    <span className="w-2 h-2 rounded-full bg-orange-400" />
                                    <span className="text-gray-700 min-w-[90px]">{formatPunchTime(b.start)}</span>
                                    <span className="text-gray-400">→</span>
                                    <span className="text-gray-700 min-w-[90px]">{formatPunchTime(b.end)}</span>
                                    <span className="text-orange-600">{formatMin(b.minutes)}</span>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Punches Modal */}
      {editingShift && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Edit Punches — {editingShift.employee_name}
                </h2>
                <p className="text-sm text-gray-500">{(() => {
                  const d = editingShift.date || (editingShift as any).shift_date || selectedDate;
                  try { return format(new Date(`${d}T12:00:00`), 'EEEE, MMMM d, yyyy'); } catch { return d; }
                })()}</p>
              </div>
              <button onClick={() => setEditingShift(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            {(editingShift.punches || []).length === 0 ? (
              <p className="text-gray-500 text-center py-6">No punches for this date</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {(editingShift.punches || []).map((p: Punch, idx: number) => {
                  const origTime = (() => { try { return format(new Date(p.punch_time), 'HH:mm'); } catch { return ''; } })();
                  return (
                    <div key={p.id} className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${p.punch_type === 'in' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {p.punch_type.toUpperCase()}
                      </span>
                      <input
                        type="time"
                        id={`punch-${p.id}`}
                        defaultValue={origTime}
                        className="border border-gray-300 rounded px-2 py-1 text-sm w-28"
                      />
                      <button
                        onClick={() => {
                          const el = document.getElementById(`punch-${p.id}`) as HTMLInputElement;
                          if (el?.value && el.value !== origTime) {
                            handleSavePunchTime(p.id, el.value);
                          } else if (el?.value === origTime) {
                            alert('No change detected');
                          }
                        }}
                        className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                      >
                        Save
                      </button>
                      <span className="text-xs text-gray-400">{p.source}</span>
                      <span className="flex-1" />
                      <button
                        onClick={() => handleDeletePunch(p.id)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="border-t border-gray-200 mt-4 pt-4">
              <p className="text-xs text-gray-500 mb-2">Add a punch:</p>
              <div className="flex gap-2">
                <input type="time" id="newPunchTime" defaultValue="12:00" className="border border-gray-300 rounded px-2 py-1 text-sm w-28" />
                <button
                  onClick={() => {
                    const el = document.getElementById('newPunchTime') as HTMLInputElement;
                    if (el?.value) handleAddPunch(editingShift.employee_id, 'in', el.value);
                  }}
                  className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                >
                  + IN
                </button>
                <button
                  onClick={() => {
                    const el = document.getElementById('newPunchTime') as HTMLInputElement;
                    if (el?.value) handleAddPunch(editingShift.employee_id, 'out', el.value);
                  }}
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                >
                  + OUT
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">Day auto-relabels IN/OUT after every change.</p>
            </div>

            <div className="flex justify-end mt-4">
              <button onClick={() => setEditingShift(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Shift Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Schedule Shift</h2>
            <p className="text-sm text-gray-500 mb-4">Add a planned shift for an employee</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                <select value={newShift.employee_id} onChange={(e) => setNewShift({ ...newShift, employee_id: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="">Select employee...</option>
                  {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input type="date" value={newShift.shift_date} onChange={(e) => setNewShift({ ...newShift, shift_date: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <input type="time" value={newShift.start_time} onChange={(e) => setNewShift({ ...newShift, start_time: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                <input type="time" value={newShift.end_time} onChange={(e) => setNewShift({ ...newShift, end_time: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleAddShift} disabled={!newShift.employee_id} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Schedule</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Break Modal */}
      {showBreakModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Break</h2>
            <p className="text-sm text-gray-500 mb-4">Insert an OUT→IN punch pair for {selectedDate}. The day's punches will re-sequence automatically.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                <select value={breakForm.employee_id} onChange={(e) => setBreakForm({ ...breakForm, employee_id: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="">Select employee...</option>
                  {shifts.map((s) => <option key={s.employee_id} value={s.employee_id}>{s.employee_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Break Start (clock out)</label>
                <input type="time" value={breakForm.break_start} onChange={(e) => setBreakForm({ ...breakForm, break_start: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Break End (clock back in)</label>
                <input type="time" value={breakForm.break_end} onChange={(e) => setBreakForm({ ...breakForm, break_end: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowBreakModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleAddBreak} disabled={!breakForm.employee_id} className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50">Add Break</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
